import { Button } from '@material-ui/core';
import { Colorize, EventNote, People, RecordVoiceOver, Videocam } from '@material-ui/icons';
import {
  IconBuildingHospital,
  IconCalendarStats,
  IconPill,
  IconPillOff,
} from '@tabler/icons-react';
import { format, isValid } from 'date-fns';
import { useRouterStore } from 'mobx-state-router';
import React, { useContext, useEffect } from 'react';

import { generateRouteUrl } from 'src/boot/routes';
import Fyi from 'src/components/general/PatientMenu/highlights/Fyi';
import { SideVisitContext } from 'src/components/pages/eventShow/SideVisitContext';
import { OBSERVABILITY_MY_DAY_TODAY, UPDATED_MDT_INTAKE_CARD } from 'src/featureFlags/currentFlags';
import { useFeatureFlag } from 'src/featureFlags/useFeatureFlag';
import { CardDivider } from 'src/myDayToday/components/CardDivider';
import { DrugScreeningStatus } from 'src/myDayToday/components/DrugScreeningStatus';
import { EventTypeLabel } from 'src/myDayToday/components/EventSubType';
import { EventSubTypeIcon } from 'src/myDayToday/components/EventSubTypeIcon';
import { InformationRow } from 'src/myDayToday/components/InformationRow';
import { MedicationRunOutLabel } from 'src/myDayToday/components/MedicationRunOutLabel';
import { PatientName } from 'src/myDayToday/components/PatientName';
import { useScheduledEventCardStyles } from 'src/myDayToday/components/ScheduledEventCard.styles';
import { ScheduledEventCardHeader } from 'src/myDayToday/components/ScheduledEventCardHeader';
import { StateBadge } from 'src/myDayToday/components/StateBadge';
import { VisitTimeFrame } from 'src/myDayToday/components/VisitTimeFrame';
import { getScheduledEventTiming } from 'src/myDayToday/components/getScheduledEventTiming';
import { useProviderId } from 'src/myDayToday/domain/useProviderId';
import { EventCardPatient } from 'src/myDayToday/types/EventCardPatient';
import { useObservability } from 'src/observability/useObservability';
import useApolloClient from 'src/shared/client/useApolloClient';
import { SETUP_EVENT_VIDEO_CONFERENCE } from 'src/stores/mutations/events';
import useRootStore from 'src/stores/useRootStore';

type PartialEvent = {
  id: string | null;
  duration: number;
  start: Date;
  subType: string;
  title?: string | null;
  type: string;
};

type ScheduledEventCardProps = {
  currentTime: Date;
  event: PartialEvent;
  patient: EventCardPatient;
  visitReason?: string;
  visitType: string;
};

const IntakeInformationRows = ({ patient }: { patient: ScheduledEventCardProps['patient'] }) => {
  const showBupeExperience = patient.primaryConcernCheck.some(primaryConcern =>
    /opioid/i.test(primaryConcern),
  );
  const showAlcoholTreatmentExperience = patient.primaryConcernCheck.some(primaryConcern =>
    /alcohol/i.test(primaryConcern),
  );

  return (
    <>
      <InformationRow
        icon={<RecordVoiceOver />}
        label="Primary concern(s)"
        value={patient.primaryConcernCheck.join(', ') || 'None'}
      />
      {showBupeExperience && (
        <InformationRow
          icon={<IconPill />}
          label="Bupe experience"
          value={patient.bupeFamiliarity}
        />
      )}
      {showAlcoholTreatmentExperience && (
        <InformationRow
          icon={<IconBuildingHospital />}
          label="Prior AUD treatment"
          value={patient.alcoholTreatmentExperience}
        />
      )}
    </>
  );
};

const GeneralInformationRows = ({
  patient,
  event,
}: {
  patient: ScheduledEventCardProps['patient'];
  event: PartialEvent;
}) => {
  return (
    <>
      <InformationRow icon={<EventNote />} label="Visit note" value={event.title ?? '--'} />
      <InformationRow
        icon={<IconCalendarStats />}
        label="Visit cadence"
        value={patient.visitCadence}
      />
      <InformationRow
        icon={<IconPillOff />}
        label="Bupe runout date"
        value={<MedicationRunOutLabel runOutDate={patient.medicationRunOutDate} />}
      />
      <InformationRow
        icon={<Colorize />}
        label="Drug screening status"
        value={
          <DrugScreeningStatus screeningStatus={patient.drugScreeningStatus} defaultLabel="--" />
        }
      />
      <InformationRow
        icon={<People />}
        label="Last visit"
        value={
          // Note for 'as Date': isValid allows us to be sure this is a Date
          isValid(patient.lastVisit) ? format(patient.lastVisit as Date, 'M/d/yyyy') : null
        }
      />
    </>
  );
};

const ScheduledEventCard = (props: ScheduledEventCardProps) => {
  const classes = useScheduledEventCardStyles();

  const router = useRouterStore();
  const rootStore = useRootStore();
  const apolloClient = useApolloClient();
  const { setSideVisit } = useContext(SideVisitContext);
  const [startVisitError, setStartVisitError] = React.useState<string | null>(null);

  const enableObservability = useFeatureFlag(OBSERVABILITY_MY_DAY_TODAY);
  const enableIntakeSpecificCard = useFeatureFlag(UPDATED_MDT_INTAKE_CARD);
  const sendStaffEvent = useObservability(enableObservability);
  const providerId = useProviderId();

  const eventTiming = getScheduledEventTiming(props.currentTime, props.event.start);

  const containerClasses = [classes.container, eventTiming];

  // There are 3 steps here:
  // - First, we create a video conference for the event
  //   (the server won't create a new one if one exists)
  // - Then we need to re-load the event data in the store
  //   otherwise the video conference data won't be seen by
  //   the video conference page
  // - Finally, we open a new tab with the video call and
  //   navigate to the event page in the current tab
  const startVisit = () => {
    setStartVisitError(null);

    sendStaffEvent({
      eventType: 'myDayToday:acceptScheduledEvent',
      userId: providerId ?? null,
      details: {
        eventId: props.event.id,
      },
    });

    apolloClient
      .mutate({
        mutation: SETUP_EVENT_VIDEO_CONFERENCE,
        variables: { id: props.event.id },
      })
      .then(() => rootStore.events.loadEvent(props.event.id))
      .then(() => {
        const videoUrl = generateRouteUrl('eventVc', { event: props.event.id });
        window.open(videoUrl, '_blank');

        if (props.event.id) {
          // Open side-by-side visit note in new tab along with e-Rx tab
          setSideVisit(props.patient.patientId, props.event.id);
          router.goTo('showPatient', { params: { id: props.patient.patientId, tab: 'erx' } });
        } else {
          // Go to event page
          router.goTo('showEvent', { params: { id: props.event.id } });
        }
      })
      .catch(e => {
        setStartVisitError(e.message?.length ? e.message : 'Unknown error');
      });
  };

  useEffect(() => {
    sendStaffEvent({
      eventType: 'myDayToday:scheduledVisitCardViewed',
      userId: providerId ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const InformationRows =
    props.visitType.includes('intake') && enableIntakeSpecificCard ? (
      <IntakeInformationRows patient={props.patient} />
    ) : (
      <GeneralInformationRows patient={props.patient} event={props.event} />
    );

  return (
    <div className={containerClasses.join(' ')} data-testid="scheduled-event-card">
      <div className="header">
        <ScheduledEventCardHeader
          currentTime={props.currentTime}
          start={props.event.start}
          timing={eventTiming}
        />
      </div>
      <div className="body">
        <div className="avatar">
          <img src={props.patient.avatarUrl} />
          <div className="patientInformation">
            <StateBadge city={props.patient.city} state={props.patient.state} />
            <Fyi patientId={props.patient.patientId} />
          </div>
        </div>
        <div className="details">
          <div className="visitType">
            <EventSubTypeIcon subType={props.event.subType} size={18} />
            <EventTypeLabel type={props.event.type} subType={props.event.subType} />
          </div>
          <div className="patientName">
            <PatientName {...props.patient} />
          </div>
          <div className="visitTimeFrame">
            <VisitTimeFrame start={props.event.start} duration={props.event.duration} />
          </div>

          <CardDivider />

          <div className="informationList">{InformationRows}</div>

          <CardDivider />

          <div className="actions">
            <div className="error">
              {startVisitError && (
                <span>An error occurred while starting the visit: {startVisitError}</span>
              )}
            </div>
            <Button className="acceptButton" onClick={startVisit}>
              <Videocam />
              Start
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduledEventCard;
